/* eslint-disable no-param-reassign */
import { createStore } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { get as _get, set as _set, isEqual as _isEqual } from 'lodash-es';
import type {
  DomainEntityDetail,
  ConfigEntityDetail,
  DomainEntityDetailAttributes,
  ConfigEntityMeta,
} from '../../types/entities';
import { replaceEmptyValuesInStringArray } from '../../utilities/replace-empty-values-in-string-array/replace-empty-values-in-string-array';

export type EntityDetailsDataStore = EntityDetailsDataStoreState &
  EntityDetailsDataStoreActions;

type EntityDetail = DomainEntityDetail | ConfigEntityDetail;

export interface EntityDetailsDataStoreState {
  entityData: Partial<EntityDetail> | undefined;
  included: Array<EntityDetail> | undefined;
  entityMeta: ConfigEntityMeta | undefined;
}

export interface EntityDetailsDataStoreActions {
  reset: () => void;
  setIncludedEntities: (data: EntityDetail[] | undefined) => void;
  addIncludedEntity: (data: EntityDetail) => void;
  setEntityData: (data: any, path?: string, locale?: string) => void;
  setEntityMeta: (meta: ConfigEntityMeta | undefined) => void;
}

export const entityDetailsDataStore = createStore<EntityDetailsDataStore>()(
  subscribeWithSelector(
    immer((set) => ({
      entityData: undefined,
      included: undefined,
      entityMeta: undefined,
      setEntityData: (data, path, locale) =>
        set((draft) => {
          const cleanInputData = replaceEmptyValuesInStringArray(data);

          if (typeof path === 'string' && path.trim().length > 0) {
            // Initialize entityData if it doesn't exist yet
            if (!draft.entityData) {
              draft.entityData = {} as Partial<EntityDetail>;
            }

            // Handle locale-specific updates
            if (typeof locale === 'string' && locale.trim().length > 0) {
              // Ensure required structure exists
              if (!draft.entityData.attributes) {
                draft.entityData.attributes =
                  {} as DomainEntityDetailAttributes;
              }

              // Ensure the translation structure exists
              if (!draft.entityData.attributes.translations) {
                draft.entityData.attributes.translations = {};
              }

              const { translations } = draft.entityData.attributes;

              // Initialize locale data if needed
              if (!translations[locale]) {
                translations[locale] = {
                  domainEntityAttributes: {},
                };
              }

              /**
               * Update only the specific locale object to prevent race conditions where
               * concurrent updates to different parts of the entity (translations vs non-localized fields)
               * might cancel each other out or cause unexpected overrides
               */
              const current = _get(translations[locale], path);
              if (!_isEqual(current, cleanInputData)) {
                _set(translations[locale], path, cleanInputData);
              }
            } else {
              // For non-locale paths, directly update the entityData
              const current = _get(draft.entityData, path);
              if (!_isEqual(current, cleanInputData)) {
                _set(draft.entityData, path, cleanInputData);
              }
            }
          } else {
            // if no path is provided, set the entire entity data
            draft.entityData = cleanInputData as Partial<EntityDetail>;
          }
        }),
      setIncludedEntities: (data) =>
        set((draft) => {
          draft.included = data;
        }),
      addIncludedEntity: (data) =>
        set((draft) => {
          if (!draft.included?.find((e: EntityDetail) => e.id === data.id))
            draft.included = [...(draft.included ?? []), data];
        }),
      setEntityMeta: (meta) =>
        set((draft) => {
          draft.entityMeta = meta;
        }),
      reset: () =>
        set((draft) => {
          draft.entityData = undefined;
          draft.included = undefined;
          draft.entityMeta = undefined;
        }),
    })),
  ),
);
